export enum PaginationKeys {
    EMISSION_CALCULATION_LOGISTICS = 'pagination_emission_calculation_logistics',
    EMISSION_CALCULATION_BUSINESS = 'pagination_emission_calculation_business',
    SHIPMENTS = 'pagination_shipments',
    IMPORTS = 'pagination_imports',
    ESTIMATES_PER_IMPORT = 'pagination_imports_estimates',
}

interface PaginationState {
    page: number
    cursorHistory: (string | undefined)[]
}

/**
 * A hook to manage pagination state across navigation
 * Saves current page and cursor history to localStorage
 */
export const usePaginationState = () => {
    const getPageKey = (key: PaginationKeys): string => `${key}_page`
    const getCursorKey = (key: PaginationKeys): string => `${key}_cursor`

    /**
     * Get saved pagination state (page number and cursor history)
     * Returns null if no state is saved
     */
    const getPaginationState = (key: PaginationKeys): PaginationState | null => {
        const savedPage = localStorage.getItem(getPageKey(key))
        const savedCursorHistory = localStorage.getItem(getCursorKey(key))

        if (!savedPage) {
            return null
        }

        const page = Number(savedPage)
        if (isNaN(page) || page < 1 || page > 1000) {
            // Invalid page number, clear and return null
            clearPaginationState(key)
            return null
        }

        let cursorHistory: (string | undefined)[] = [undefined]
        if (savedCursorHistory) {
            try {
                cursorHistory = JSON.parse(savedCursorHistory)
            } catch (error) {
                // Invalid cursor history, use default
                console.error(
                    'Failed to parse cursor history from localStorage for key',
                    getCursorKey(key),
                    error,
                )
                cursorHistory = [undefined]
            }
        }

        return { page, cursorHistory }
    }

    /**
     * Save pagination state (page number and cursor history)
     */
    const savePaginationState = (
        key: PaginationKeys,
        page: number,
        cursorHistory: (string | undefined)[],
    ) => {
        localStorage.setItem(getPageKey(key), page.toString())
        localStorage.setItem(getCursorKey(key), JSON.stringify(cursorHistory))
    }

    /**
     * Clear saved pagination state
     */
    const clearPaginationState = (key: PaginationKeys) => {
        localStorage.removeItem(getPageKey(key))
        localStorage.removeItem(getCursorKey(key))
    }

    /**
     * Restore and immediately clear pagination state (one-time use)
     * Returns the saved state and clears it from localStorage
     */
    const restoreAndClearPaginationState = (key: PaginationKeys): PaginationState | null => {
        const state = getPaginationState(key)
        if (state) {
            clearPaginationState(key)
        }
        return state
    }

    return {
        getPaginationState,
        savePaginationState,
        clearPaginationState,
        restoreAndClearPaginationState,
    }
}
